import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Stamp, Landmark, Upload, ArrowLeft } from "lucide-react";
import { DocUploadList, NOTARY_DOC_TYPES, docsReady, docsMeta } from "@/components/DocUploadList";

const TN_COUNTIES = ["Anderson","Bedford","Benton","Bledsoe","Blount","Bradley","Campbell","Cannon","Carroll","Carter","Cheatham","Chester","Claiborne","Clay","Cocke","Coffee","Crockett","Cumberland","Davidson","Decatur","DeKalb","Dickson","Dyer","Fayette","Fentress","Franklin","Gibson","Giles","Grainger","Greene","Grundy","Hamblen","Hamilton","Hancock","Hardeman","Hardin","Hawkins","Haywood","Henderson","Henry","Hickman","Houston","Humphreys","Jackson","Jefferson","Johnson","Knox","Lake","Lauderdale","Lawrence","Lewis","Lincoln","Loudon","Macon","Madison","Marion","Marshall","Maury","McMinn","McNairy","Meigs","Monroe","Montgomery","Moore","Morgan","Obion","Overton","Perry","Pickett","Polk","Putnam","Rhea","Roane","Robertson","Rutherford","Scott","Sequatchie","Sevier","Shelby","Smith","Stewart","Sullivan","Sumner","Tipton","Trousdale","Unicoi","Union","Van Buren","Warren","Washington","Wayne","Weakley","White","Williamson","Wilson"];

export default function NotaryRequest() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const enhanced = sp.get("type") === "enhanced";
  const [me, setMe] = useState(null);
  const [cfg, setCfg] = useState({ document_categories: [], mobile_fee: 35 });
  const [f, setF] = useState({ name: "", phone: "", email: "", county: "", address: "", preferred_datetime: "", service_type: enhanced ? "Estate Planning / Enhanced Notary" : "Mobile Notary", num_signers: enhanced ? 2 : 1, notes: "" });
  const [cats, setCats] = useState(enhanced ? ["Estate Planning Documents"] : []);
  const [docs, setDocs] = useState([]);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/notary/config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  // Existing client: use the authenticated account — never force re-entry / recreation.
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      if (data && data.role === "client") {
        setMe(data);
        setF((s) => ({ ...s, name: s.name || data.name || "", phone: s.phone || data.phone || "", email: s.email || data.email || "" }));
      }
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!f.county) { setArea(null); return; }
    const t = setTimeout(() => { api.get("/notary/service-area", { params: { county: f.county } }).then(({ data }) => setArea(data)).catch(() => {}); }, 400);
    return () => clearTimeout(t);
  }, [f.county]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleCat = (c) => setCats((s) => (s.includes(c) ? s.filter((x) => x !== c) : [...s, c]));

  const dirty = !!(f.name || f.phone || f.email || f.county || f.address || f.preferred_datetime || f.notes || cats.length || docs.length);
  const goBack = () => {
    if (busy) return;
    if (dirty && !window.confirm("Leave this page? Your information has not been submitted.")) return;
    nav(me ? "/client/start" : "/notary-services");
  };

  const upload = async (e) => { e.target.value = ""; };

  const submit = async () => {
    if (busy) return;
    if (!f.name || !f.email || !f.phone) { toast.error("Name, phone and email are required"); return; }
    if (!docsReady(docs)) { toast.error("Confirm each image is upright and choose a document type for every file."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/notary/requests", {
        ...f, num_signers: Number(f.num_signers) || 1, document_categories: cats, documents: docs.map((d) => d.id), documents_meta: docsMeta(docs),
        ...(enhanced ? { enhanced_evidence: true, document_category: "Estate Planning Documents", id_evidence_policy: "method_only" } : {}),
      });
      // Only redirect to the confirmation page after the request is successfully created.
      nav("/notary/confirmation", { state: { email: f.email, area: data.service_area } });
    } catch {
      toast.error("Your request was not submitted. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5 py-6" data-testid="notary-request-page">
      <button type="button" data-testid="notary-back" onClick={goBack} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> {me ? "Back to Create an Order" : "Back to Notary Services"}
      </button>
      <div className="flex items-center gap-3">{enhanced ? <Landmark className="h-7 w-7 text-amber-600" /> : <Stamp className="h-7 w-7 text-indigo-600" />}<div>
        <h2 className="font-display text-2xl font-bold text-slate-900">{enhanced ? "Estate Planning / Enhanced Notary Request" : "Notary Service Request"}</h2>
        <p className="text-slate-500">Tell us about your notarization. Pricing is confirmed by our team after review.</p>
      </div></div>
      {enhanced && (
        <div data-testid="notary-enhanced-banner" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <strong>Enhanced evidence signing.</strong> This service captures signer verification and signature-page evidence for high-stakes estate documents. Our team will confirm the exact requirements with you.
        </div>
      )}
      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        {[["name", "Name"], ["phone", "Phone"], ["email", "Email"]].map(([k, l]) => (
          <div key={k}><Label>{l}<span className="text-red-500"> *</span></Label>
            <Input data-testid={`notary-${k}`} value={f[k]} onChange={(e) => set(k, e.target.value)} /></div>
        ))}
        <div><Label>County</Label>
          <select data-testid="notary-county" value={f.county} onChange={(e) => set("county", e.target.value)} className="mt-1 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
            <option value="">Select a county…</option>
            {TN_COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div><Label>Address / Location</Label><Input data-testid="notary-address" value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
        <div><Label>Preferred Date / Time</Label><Input data-testid="notary-preferred_datetime" type="datetime-local" value={f.preferred_datetime} onChange={(e) => set("preferred_datetime", e.target.value)} /></div>
        {area && (
          <div data-testid="notary-area-message" className={`rounded-lg border p-3 text-sm ${area.inside ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>{area.message}</div>
        )}
        <div>
          <Label>Documents</Label>
          <div className="mt-1 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {cfg.document_categories.map((c) => (
              <label key={c} className="flex items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm text-slate-700">
                <input type="checkbox" data-testid={`notary-doc-${c}`} checked={cats.includes(c)} onChange={() => toggleCat(c)} className="h-4 w-4" />{c}
              </label>
            ))}
          </div>
          {cats.includes("Power of Attorney") && (
            <div data-testid="notary-poa-notice" className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <strong>Two Witnesses Required at Signing for TN POA.</strong> Tennessee requires two witnesses to be present when signing a Power of Attorney. This is for your information and does not affect your submission.
            </div>
          )}
          <div className="mt-2"><DocUploadList docs={docs} setDocs={setDocs} docTypes={NOTARY_DOC_TYPES} accent="indigo" /></div>
        </div>
        <div><Label>Notes (optional)</Label><Textarea data-testid="notary-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
        <Button data-testid="notary-submit" onClick={submit} disabled={busy} className="w-full bg-indigo-600 text-white hover:bg-indigo-700">{busy ? "Submitting…" : "Submit request"}</Button>
      </div>
    </div>
  );
}
